import { Box, Stack, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { lazy, Suspense, useCallback, useState, useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTemplateFields } from '../../hooks/useTemplateFields';
import * as poiState from '../../../../state';
import { measurementsByField } from '@/components/Analysis/modules/measurementTool/state';
import { TemplateFieldsForm } from '../TemplateFieldsForm';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import {
  AllPointOfInterestsForDataLoaderDocument,
  PaintDamageCountByAlarmLevelDocument,
  PointOfInterestsCountByDueDateDocument,
  PointOfInterestsCountByInspectionPriorityDocument,
  SelectedPointOfInterestDocument,
  useUpdatePoiTemplateFieldsMutation,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useBlisterData } from '../../hooks/useBlisterData';
import { BlisterFormFields } from '../BlisterFormFields';
import { useTriggerPoiEditing } from '../../hooks';
import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { convertTemplateNameToPoiType, getPoiTemplateKey } from '@/utils';
import { useFeatureFlag } from '@/hooks';
import { BLISTER_TYPE, PIT_TYPE } from '@/constants';
import { propsToPoiLineMeasurement } from '@/components/Analysis/modules/measurementTool/hooks/utils';
import { useUncertainField } from '../../hooks/useUncertainField';
import { useFetchWorkscopeDefects } from '../../hooks/useFetchWorkscopeDefects';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { checkIfVoi } from '@/utils/poi';
import * as state from '@/components/Analysis/state';
import { editingCuboids as editingCuboidsAtom } from '@/components/Analysis/modules/cuboids';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks';

const BlisterMeasurementLazy = lazy(() =>
  import('../../../../createOrEdit').then(({ BlisterMeasurement }) => ({
    default: BlisterMeasurement,
  }))
);

export const UpdateTemplateFields = () => {
  const { user } = useAuth0();
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const template = editPointOfInterest?.template;
  const { populateMeasurementFromPOI } = useMeasurementTool();
  const setManualBlisterHeightExists = useSetAtom(poiState.manualBlisterHeightExists);
  const [loading, setLoading] = useState(false);
  const poi = editPointOfInterest?.pointOfInterest;
  const measurementsMap = useAtomValue(measurementsByField);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const editingCuboids = useAtomValue(editingCuboidsAtom);

  const poiType = convertTemplateNameToPoiType(getPoiTemplateKey(editPointOfInterest?.template));

  const { stopAddingCuboid } = useAnalysisCuboidActions();

  const isInspector = useFeatureFlag('api-inspector');
  const hasEditRole = useFeatureFlag(['ait-engineer']);
  const isInspectorOnly = isInspector && !hasEditRole;
  const isCreator = poi?.createdByUser?.id === user?.sub;
  const canEditPoiDetails = hasEditRole || (isInspectorOnly && isCreator);

  useEffect(() => {
    if (
      editPointOfInterest?.template?.fields?.some((field) => field.isMeasurementToolEnabled) &&
      poi?.lineMeasurement
    ) {
      populateMeasurementFromPOI(poi.lineMeasurement);
    }
  }, [
    editPointOfInterest?.template?.fields,
    poi?.lineMeasurement,
    populateMeasurementFromPOI,
    editPointOfInterest,
    poi,
    template,
  ]);

  const filteredTemplateFields = (editPointOfInterest?.template?.fields || []).filter(
    (field) => field.name !== 'Comments'
  );
  const dataHook = useTemplateFields({
    selectedPoiInfo: poi,
    templateFields: filteredTemplateFields,
  });

  const isPoiTypeOfVoi = checkIfVoi(allVoiTemplates, poiType);

  const { checkByType } = useIsPoiWorkScope();
  const isWorkScope = checkByType(poiType || '');

  const shouldIncludeCuboids = useMemo(() => {
    return isWorkScope || isPoiTypeOfVoi;
  }, [isWorkScope, isPoiTypeOfVoi]);

  const {
    localBlisterData,
    blisterFieldsData,
    pointSelectMessage,
    updatePoiBlister,
    onMeasure,
    onSelectPoint,
    clearBlisterData,
    handleManualHeightChange,
    handleMeasureIconClick,
    handleChangeControlledField,
  } = useBlisterData(dataHook, poiType);

  const { showSnackBar } = useSnackBarMessage({});

  const { rightDrawerWidth, doublePanelWidth } = useDrawerWidths();
  const navbarHeight = useNavbarHeight();

  const [mutate] = useUpdatePoiTemplateFieldsMutation();
  const [updatePointOfInterestMutation] = useUpdatePointOfInterestMutation();

  const { cancelEditing } = useTriggerPoiEditing();
  const { inputAdormantHandlerMap, cancelMeasuring } = useMeasurementTool();
  const blisterHeight = String(localBlisterData?.maxHeight);
  const blisterHeightIndex = localBlisterData?.blisterHeightIndex;
  const onCancel = useCallback(() => {
    cancelEditing();
    cancelMeasuring();
    stopAddingCuboid();
    setManualBlisterHeightExists(false);
  }, [stopAddingCuboid, cancelEditing, cancelMeasuring, setManualBlisterHeightExists]);

  // When an API Inspector (without edit role) wants to re-generate the blister on Blister/Pit,
  // they would not have the ability to edit the template fields.
  // Override: when user has edit access (edit role or inspector+creator), enable fields and save.
  const shouldDisableTemplateFieldsForm = useMemo(() => {
    if (canEditPoiDetails) return false;
    const excludedTypes = [PIT_TYPE, BLISTER_TYPE];
    return excludedTypes.includes(poiType) && isInspector;
  }, [canEditPoiDetails, isInspector, poiType]);

  useEffect(() => {
    if (localBlisterData?.maxHeight) {
      updatePoiBlister(localBlisterData?.maxHeight, false, localBlisterData.blisterHeightIndex);
    }
  }, [localBlisterData?.maxHeight, updatePoiBlister, localBlisterData?.blisterHeightIndex]);

  const cuboidsInput = useMemo(() => {
    return shouldIncludeCuboids
      ? editingCuboids.map((cuboid) => ({
          position: cuboid.position,
          rotation: cuboid.rotation,
          scale: cuboid.scale,
        }))
      : undefined;
  }, [editingCuboids, shouldIncludeCuboids]);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    let poiInput;
    let updatePoi;
    updatePoi = mutate;
    poiInput = {
      input: {
        pointOfInterestId: poi?.id ?? '',
        templateId: editPointOfInterest.template?.id,
        templateFieldData: Object.entries(dataHook.state).reduce<Record<string, string | number>>(
          (acc, [key, value]) => {
            acc[key] = value;
            return acc;
          },
          {}
        ),
        cuboids: cuboidsInput,
        lastModifiedFor: 'Update Detail Fields',
        lineMeasurement: editPointOfInterest?.template?.fields?.some(
          (field) => field.isMeasurementToolEnabled
        )
          ? propsToPoiLineMeasurement(measurementsMap)
          : undefined,
      },
    };

    if (poi?.id && localBlisterData) {
      // Removing __typename from blister
      const blister = JSON.parse(JSON.stringify(localBlisterData), (key, value) => {
        return key === '__typename' ? undefined : value;
      });
      updatePoi = updatePointOfInterestMutation;
      poiInput = {
        input: {
          pointOfInterestId: poi.id,
          blister,
          templateId: editPointOfInterest.template?.id,
          type: poiType,
          templateFieldData: Object.entries(dataHook.state).reduce<Record<string, string | number>>(
            (acc, [key, value]) => {
              acc[key] = value;
              return acc;
            },
            {}
          ),
          lastModifiedFor: 'Update Detail Fields',
        },
      };
    }

    updatePoi({
      variables: poiInput,
      refetchQueries: [
        AllPointOfInterestsForDataLoaderDocument,
        SelectedPointOfInterestDocument,
        PointOfInterestsCountByInspectionPriorityDocument,
        PointOfInterestsCountByDueDateDocument,
        PaintDamageCountByAlarmLevelDocument,
      ],
    })
      .then(() => {
        showSnackBar('Point of interest updated', 'success');
        cancelEditing();
        cancelMeasuring();
        stopAddingCuboid();
        setManualBlisterHeightExists(false);
      })
      .catch(() => {
        showSnackBar('Could not update point of interest', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    stopAddingCuboid,
    mutate,
    poi?.id,
    editPointOfInterest.template?.id,
    editPointOfInterest.template?.fields,
    dataHook.state,
    measurementsMap,
    localBlisterData,
    updatePointOfInterestMutation,
    poiType,
    showSnackBar,
    cancelEditing,
    cancelMeasuring,
    setManualBlisterHeightExists,
    cuboidsInput,
  ]);

  const uncertainField = useUncertainField({ dataHook });

  useFetchWorkscopeDefects({
    skip: !isWorkScope && !isPoiTypeOfVoi,
    onCompleted: (response) => {
      const defects: Record<string, number> = response?.dynamicBlockCalculation;
      const fields = template?.fields ?? [];

      Object.entries(defects).forEach((defect) => {
        const key = defect?.[0];
        const value = defect?.[1];
        const found = fields.find((field) => field.key === key);
        if (found) {
          dataHook.handleChange(found?.key ?? '', value?.toFixed(2));
        }
      });
    },
  });

  return (
    <Stack direction="row" sx={{ height: '100%' }}>
      {localBlisterData && (
        <Stack width={doublePanelWidth - rightDrawerWidth}>
          <Suspense>
            <BlisterMeasurementLazy
              poiToAdd={poi}
              onMeasure={onMeasure}
              clearBlisterData={clearBlisterData}
              onSelectPoint={onSelectPoint}
              pointSelectMessage={pointSelectMessage}
              uncertainField={uncertainField}
              handleChangeControlledField={handleChangeControlledField}
              blisterData={{ estimatedHeight: blisterHeight, blisterHeightIndex }}
            />
          </Suspense>
        </Stack>
      )}
      <Stack width={rightDrawerWidth}>
        <StyledForm
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
          style={{ borderLeft: '1px solid #ddd' }}
        >
          <Stack
            sx={{ height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`, overflowY: 'auto' }}
          >
            <Box
              sx={{
                '& > *': { mb: 1 },
                p: 2,
                mb: 8,
                flex: 1,
              }}
            >
              <Typography variant="h6">Edit Details</Typography>
              {localBlisterData && (
                <BlisterFormFields
                  blisterData={blisterFieldsData}
                  uncertainField={uncertainField}
                  handleManualHeightChange={handleManualHeightChange}
                  handleMeasureIconClick={handleMeasureIconClick}
                  allDisabled={shouldDisableTemplateFieldsForm}
                />
              )}
              <TemplateFieldsForm
                dataHook={dataHook}
                template={editPointOfInterest.template}
                formState={editPointOfInterest?.formState}
                inputAdormantHandlerMap={inputAdormantHandlerMap}
                allDisabled={shouldDisableTemplateFieldsForm}
                handleChangeControlledField={handleChangeControlledField}
              />
            </Box>

            <SaveCancelFormButtons
              cancelButtonLabel="Cancel"
              buttonLabel="Save"
              loadingLabel="Updating..."
              onCancel={onCancel}
              loading={loading}
              sx={saveCancelButtonCommonStyles.sx}
              saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
              cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
              saveButtonDisabled={shouldDisableTemplateFieldsForm}
            />
          </Stack>
        </StyledForm>
      </Stack>
    </Stack>
  );
};
